import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Paper,
    Typography,
    useTheme,
} from '@mui/material';
import { HEADER, NAV } from 'src/layouts/config-layout';
import { useNavPinned } from 'src/layouts/dashboard/nav-pinned-context';
import { FlagIcon } from 'src/components/iconify/flagIcon';
import HorizontalPager from 'src/components/horizontal-pager';
import { CountryFilterProps, CountryItem } from '../types';

export function CountryFilter({
    countries,
    selectedCountry,
    onCountryChange,
    backgroundColor = 'transparent',
    enableDeepLinking = true,
    renderCountryLabel,
    stickyTop,
}: CountryFilterProps & { stickyTop?: number }) {
    const theme = useTheme();
    const navPinned = useNavPinned();
    const navWidth = navPinned ? NAV.W_VERTICAL_EXPANDED : NAV.W_VERTICAL;

    // Map region names to country codes
    const getCountryCodeFromRegionName = (regionName: string, countries: any[]): string | null => {
        if (!countries || countries.length === 0) return null;

        // Try exact match first
        const exactMatch = countries.find(
            (c) => c.country_name?.toLowerCase() === regionName?.toLowerCase()
        );
        if (exactMatch) return exactMatch.country_code;

        // Try partial match (e.g., "France" matches "France")
        const partialMatch = countries.find(
            (c) => regionName?.toLowerCase().includes(c.country_name?.toLowerCase()) ||
                c.country_name?.toLowerCase().includes(regionName?.toLowerCase())
        );
        if (partialMatch) return partialMatch.country_code;

        // Fallback: try to extract code from region name if it looks like a code
        if (regionName && regionName.length === 2) {
            return regionName.toUpperCase();
        }

        return null;
    };


    if (countries.length <= 1) {
        return null;
    }

    const codeToName = useMemo(() => {
        const map = new Map<string, string>();
        countries.forEach((c) => {
            map.set(String(c.country_code).toUpperCase(), c.country_name);
        });
        return map;
    }, [countries]);

    const [didApplyDeepLink, setDidApplyDeepLink] = useState(false);

    useEffect(() => {
        if (didApplyDeepLink || !enableDeepLinking) return;
        try {
            const params = new URLSearchParams(window.location.search);
            const codeParam = params.get('country');
            if (codeParam) {
                const countryName = codeToName.get(codeParam.toUpperCase());
                if (countryName) {
                    onCountryChange(selectedCountry === countryName ? selectedCountry : countryName);
                }
            }
        } catch { }
        setDidApplyDeepLink(true);
    }, [codeToName, onCountryChange, didApplyDeepLink, selectedCountry, enableDeepLinking]);

    return (
        <>
            <Box sx={{ height: 44 }} />
            <Paper
                sx={{
                    p: 0,
                    px: '24px',
                    height: '42px',
                    width: '100%',
                    bgcolor: 'white',
                    overflow: 'hidden',
                    borderBottom: '1px solid #EAEAEA',
                    position: 'fixed',
                    top: stickyTop ?? HEADER.H_DESKTOP * 2,
                    left: { lg: navWidth },
                    right: 0,
                    borderRadius: 0,
                    zIndex: theme.zIndex.appBar - 1,
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <HorizontalPager
                    spacing={20}
                    items={countries}
                    getItemKey={(c) => c.country_code}
                    activeIndex={selectedCountry ? countries.findIndex((c) => c.country_name === selectedCountry) : null}
                    renderItem={(c: CountryItem) => {
                        const countryCode = getCountryCodeFromRegionName(
                            c.country_name,
                            countries
                        );

                        return (
                            <Button
                                key={c.country_code}
                                variant="text"
                                color="inherit"
                                disableRipple
                                disableFocusRipple
                                disableTouchRipple
                                onClick={() => {
                                    if (selectedCountry === c.country_name) return;
                                    onCountryChange(c.country_name);
                                }}
                                sx={{
                                    px: '6px',
                                    py: '14px',
                                    height: '42px',
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        boxShadow: 'none',
                                        opacity: .7,
                                    },
                                    width: 'fit-content',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    borderRadius: 0,
                                    borderBottom: selectedCountry === c.country_name ? '3px solid black' : '2px solid transparent',
                                    boxSizing: 'border-box',
                                    alignSelf: 'stretch',
                                }}
                            >
                                {countryCode && <FlagIcon code={countryCode} />}

                                <Typography
                                    sx={{
                                        fontWeight: 400,
                                        fontSize: ".875rem",
                                    }}
                                    color={selectedCountry === c.country_name ? "black" : "#919EAB"}
                                >
                                    {renderCountryLabel
                                        ? renderCountryLabel(c, selectedCountry === c.country_name)
                                        : c.country_name}
                                </Typography>

                            </Button>
                        );
                    }}
                />
            </Paper>
        </>
    );
}
